import { useCallback } from "react";
import {
  List,
  ActionIcon,
  Flex,
  Divider,
  Text,
  useMantineTheme,
  Box,
} from "@mantine/core";
import { IconPlaylistAdd } from "@tabler/icons";
import ActionCard from "src/components/ActionCard";
import { openModal, closeAllModals, useModals } from "@mantine/modals";
import { CardRadius, HeightCard } from "src/utils/constants";
import {
  GetTerminalsByLocationQuery,
  useDeletePaymentTerminalMutation,
  useGetTerminalsByLocationQuery,
} from "src/generated/graphql";
import PaymentTerminalDetail from "./PaymentTerminalDetail";
import useTransitionMenu from "src/hooks/useTransitionMenu";

interface IFeeListProps {
  locationId: string;
}

export type IPaymentTerminalElement =
  NonNullable<GetTerminalsByLocationQuery>["paymentTerminalMany"][0];

function PaymentTerminal({ locationId }: IFeeListProps) {
  const { TransitionMenu, setActiveOption } = useTransitionMenu();
  const modals = useModals();
  const theme = useMantineTheme();
  let color = theme.colors.secondaryTextColor[6];

  const { data: terminals, refetch } = useGetTerminalsByLocationQuery({
    variables: { locationId },
    skip: !locationId,
  });

  const [deleteTerminal] = useDeletePaymentTerminalMutation({
    onCompleted: () => {
      refetch();
      closeAllModals();
    },
  });

  const addActionIcon = (
    <ActionIcon size="lg" onClick={() => onCreate()}>
      <IconPlaylistAdd color={color} />
    </ActionIcon>
  );

  const onCreate = useCallback(() => {
    openModal({
      closeOnClickOutside: false,
      title: "Create Payment Terminal",
      children: (
        <PaymentTerminalDetail refetch={refetch} locationId={locationId} />
      ),
    });
  }, [refetch, locationId]);

  const onEdit = useCallback(
    (detail: IPaymentTerminalElement) => {
      modals.openModal({
        closeOnClickOutside: false,
        title: `Edit Payment Terminal`,
        children: (
          <PaymentTerminalDetail
            refetch={refetch}
            locationId={locationId}
            formData={detail}
          />
        ),
      });
    },
    [refetch, locationId]
  );

  const onDelete = (id: string) => {
    deleteTerminal({ variables: { id } });
  };

  const handleOpenListMenu = (option: IPaymentTerminalElement) => {
    setActiveOption(option);
  };

  return (
    <ActionCard
      title="Payment Terminal"
      rightIconButton={addActionIcon}
      height={HeightCard.sm}
      radius={CardRadius.sm}
      size={"sm"}
      childXs={{ background: theme.colors.secondary[6] }}
      overflow="hidden"
    >
      <Box>
        {!terminals?.paymentTerminalMany.length ? (
          <Flex justify="center" align="center">
            <Text color={color}>Click</Text>&nbsp;{addActionIcon}&nbsp;
            <Text color={color}>to add a Payment Terminal</Text>
          </Flex>
        ) : (
          <List w="100%" listStyleType="none" style={{ color }}>
            {terminals?.paymentTerminalMany.map((item, index) => (
              <List.Item
                w="100%"
                icon={null}
                key={item.id}
                c={color}
                sx={{
                  "&": {
                    position: "relative",
                  },
                  "& ::marker": {
                    content: "none",
                  },
                  "& .mantine-List-itemWrapper": {
                    display: "flex",
                  },
                  "& .mantine-List-itemWrapper > span > div": {
                    alignItems: "center",
                  },
                }}
              >
                {!!index && (
                  <Divider style={{ borderColor: theme.colors.gray[2] }} />
                )}
                <Flex
                  align="center"
                  justify="space-between"
                  sx={{
                    padding: "4px 0",
                  }}
                >
                  <Text size={17} fw={500}>
                    {item.name}
                  </Text>
                  <Flex align="center">
                    <Flex gap={"xl"} mr={50}>
                      <Text size={17} fw={500} align="center">
                        {`Serial ID: ${item.physicalId}`}
                      </Text>
                    </Flex>
                    <TransitionMenu
                      onClick={() => handleOpenListMenu(item)}
                      onEdit={() => onEdit(item)}
                      onDelete={onDelete}
                      id={item.id}
                    />
                  </Flex>
                </Flex>
              </List.Item>
            ))}
          </List>
        )}
      </Box>
    </ActionCard>
  );
}

export default PaymentTerminal;
